import cx from 'classnames'
import { useRef, useState, useEffect } from 'react'

import { useClickOutside } from '../../../../hooks'
import { Checkbox } from '../../../shared/NarusMaterial'
import { useIGAO } from '../../context'
import { SaveOnPress } from '../../shared'
import {
  uid,
  cancelledOrCompleted,
  detailsFields,
  filterItemsByStatus,
  notEditableItem,
  isFavorable,
  favorable,
  capitalize,
  withDivider
} from '../../utils'
import { Issue } from '../issues'
import { Multi as MultiSelect } from '../selects'

import styles from './item.module.scss'

export function Multi ({
  item,
  handleCheckbox,
  prevItem,
  handleInputSubmit,
  handleInput,
  createDescription,
  handleBatch,
  selectedIssueId,
  setSelectedIssueId
}) {
  const { filters, pageData: { user, lists } } = useIGAO()
  const saveIssueRef = useRef(null)
  const wrapperRef = useRef(null)
  const issueRef = useRef()
  const { createIssue } = useIGAO()
  const [ editId, setEditId ] = useState(null)
  const [ newIssueTemp, setNewIssueTemp ] = useState(null)
  const [ filteredIssue, setFilteredIssue ] = useState(null)
  const [ disableCheck, setDisableCheck ] = useState(true)
  const checkDisabled = item?.issues?.length > 0
  const isChecked = isFavorable(item)
  const favorableAnswer = favorable(item.unfavorable)

  useClickOutside(wrapperRef, () => {
    setEditId(null)
  })

  issueRef.current = { issue: newIssueTemp }

  useEffect(() => {
    setFilteredIssue(
      filterItemsByStatus(item.issues, filters).sort(
        (prev, next) => notEditableItem(prev) - notEditableItem(next)
      )
    )
    setDisableCheck(
      checkDisabled ? item.issues.some(i => !cancelledOrCompleted(i)) : false
    )

    return () => {
      setDisableCheck(true)
      setFilteredIssue(null)
    }
  }, [ item, filters, checkDisabled ])

  const createNewMockIssue = (relations = []) => {
    const assignee = {
      ...user,
      key: 'assignee'
    }
    const status = {
      ...lists.status.find(s => s.id === 'in-progress'),
      key: 'status'
    }

    const _trend = {
      key: 'trend'
    }

    const newIssue = {
      id: uid(),
      relations,
      goal: item.goal,
      draft: true,
      domain: item.domain,
      details: [ status, assignee, _trend ],
      comment: [],
      createdAt: new Date().toISOString()
    }

    setNewIssueTemp(newIssue)
    issueRef.current.issue = newIssue
  }

  const pushIssue = () => {
    const { issue } = issueRef.current
    const lengthWithoutOptional = rel =>
      rel?.filter(r => r.key.indexOf('optional') === -1).length
    if (
      lengthWithoutOptional(issue?.relations) ===
        lengthWithoutOptional(item.relations) &&
      !isChecked
    ) {
      const existedOptional =
        item.relations.some(r => r.key.indexOf('optional') !== -1) &&
        !issue.relations.some(r => r.key.indexOf('optional') !== -1)
      if (existedOptional) {
        issue.relations.push({
          key: 'details_optional',
          value: ''
        })
      }
      const tempIssueWithDescription = createDescription(item, issue)
      createIssue(item.id, tempIssueWithDescription, createNewMockIssue)
    }
  }

  const applyChanges = (id, key, issue) => {
    const tempIssue = { ...issue }
    const existed = tempIssue?.relations?.find(r => r.key === key)
    if (detailsFields.includes(key)) {
      if (!existed) {
        tempIssue.relations.push({
          key,
          value: id
        })
      } else {
        existed.value = id
      }
    } else if (!existed) {
      tempIssue.relations.push({
        id,
        key,
        name: lists[key].find(l => l.id === id).name
      })
    } else {
      existed.id = id
      existed.name = lists[key].find(l => l.id === id).name
    }
    return tempIssue
  }

  const handleEdit = ({ id, key, type = 'select', value }) => {
    if (type === 'select') {
      if (newIssueTemp) setNewIssueTemp(applyChanges(id, key, newIssueTemp))
      else {
        createNewMockIssue([
          {
            id,
            key,
            name: lists[key].find(l => l.id === id).name
          }
        ])
      }
      pushIssue()
    }
    if (type === 'input') {
      if (newIssueTemp) applyChanges(value, key, newIssueTemp)
      else {
        createNewMockIssue([{
          value,
          key
        }])
      }
      handleInput(id, key, value, item.id)
    }
  }

  const handleKeyPress = e => {
    if (e.keyCode === 13 || e.charCode === 13) {
      e.preventDefault()
      pushIssue()
    }
  }

  const onFocus = () => {
    if (!newIssueTemp) {
      createNewMockIssue()
    }
  }

  return (
    <>
      {withDivider(prevItem, filters)}
      <div
        id={item.id}
        className={cx(styles.itemWrap, {
          [styles.filled]: filteredIssue?.length > 0
        })}
      >
        <SaveOnPress
          onKeyPress={e => handleKeyPress(e)}
          saveRef={saveIssueRef}
          focus={false}
        >
          <div className={cx(styles.item, styles.multi)}>
            <div className={cx(styles.innerBody, styles.column)}>
              <div className={cx(styles.itemLabel, styles.multi)}>
                <button
                  className={cx({
                    [styles.active]: !disableCheck
                  })}
                  data-cy='favorable-btn'
                  onClick={() => {
                    if (!disableCheck) {
                      handleCheckbox(item.id, favorableAnswer)
                    }
                  }}
                >
                  {item?.label}
                </button>
              </div>
              <div className={cx(styles.selects, styles.long)}>
                <div>
                  {item.relations.map(rel => (
                    <MultiSelect
                      key={`${rel.id}-${item.label}`}
                      issues={item.issues}
                      relation={rel}
                      onFocus={onFocus}
                      lists={lists}
                      itemId={item.id}
                      disabled={isChecked}
                      handler={handleEdit}
                      selected={newIssueTemp?.relations?.find(
                        r => r.key === rel.key
                      )}
                    />
                  ))}
                </div>
              </div>
            </div>
            <div
              style={{
                width: '60px',
                padding: '8px',
                borderRadius: '10px'
              }}
              className={cx({ [styles.diff]: Object.prototype.hasOwnProperty.call(item?.diff ?? {}, 'answer') })}
            >
              <Checkbox
                label={capitalize(favorableAnswer)}
                onChange={() => {
                  if (!disableCheck) {
                    handleCheckbox(item.id, favorableAnswer)
                  }
                }}
                value={isChecked}
                labelClassName={styles.igaoLabel}
                disabled={disableCheck}
              />
            </div>
          </div>
        </SaveOnPress>
      </div>
      {filteredIssue?.length > 0 && (
        <div
          className={styles.issueList}
          data-cy='issue-list'
        >
          {filteredIssue?.map(issue => (
            <Issue
              key={issue.id}
              item={item}
              issue={issue}
              editId={editId}
              setEditId={setEditId}
              selected={selectedIssueId === issue.id}
              setSelectedIssueId={setSelectedIssueId}
              handleBatch={handleBatch}
              handleInputSubmit={handleInputSubmit}
            />
          ))}
        </div>
      )}
    </>
  )
}
